import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { EyeOff, Eye } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

const CLASS_LABEL = {
  internal_test: "Internal test",
  demo_seed: "Demo data",
  internal_seed: "Internal seed",
  needs_ownership_check: "Needs ownership check",
  genuine: "Genuine merchant",
};

const VisibilityDialog = ({ target, onClose, onDone }) => {
  const [reason, setReason] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!target) return null;
  const hiding = !target.hidden;
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/store-visibility/${target.id}`, { hidden: hiding, reason, confirm_genuine: confirm });
      toast.success(hiding ? "Store hidden from public" : "Store visible again");
      onDone();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(d?.message || (typeof d === "string" ? d : "Could not update visibility"));
    } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="visibility-dialog">
        <DialogHeader>
          <DialogTitle>{hiding ? "Hide" : "Unhide"} {target.name}</DialogTitle>
          <DialogDescription>Nothing is deleted. Billing, sales and ownership stay unchanged. This is logged.</DialogDescription>
        </DialogHeader>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required)" data-testid="visibility-reason-input" />
        {hiding && target.requires_confirmation && (
          <label className="flex items-start gap-2 text-sm text-amber-800" data-testid="visibility-confirm-genuine">
            <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} data-testid="visibility-confirm-checkbox" />
            I confirm this store is not a genuine merchant listing (classified as {CLASS_LABEL[target.classification]}).
          </label>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="visibility-cancel-btn">Cancel</Button>
          <Button onClick={submit} disabled={busy || reason.trim().length < 5 || (hiding && target.requires_confirmation && !confirm)} data-testid="visibility-submit-btn">
            {hiding ? "Hide from public" : "Make visible"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const StoreVisibilityPanel = () => {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [target, setTarget] = useState(null);
  const load = () => api.get("/admin/store-visibility").then((r) => setRows(r.data.stores || [])).catch(() => setRows([]));
  useEffect(() => { if (user?.role === "super_admin") load(); }, [user?.role]);
  if (user?.role !== "super_admin") return null;
  return (
    <div className="pf-card p-5 mb-6" data-testid="store-visibility-panel">
      <h3 className="font-display text-xl font-semibold">Public visibility review</h3>
      <p className="text-sm text-muted-foreground mb-3">Test, demo and test-looking stores. Hidden stores disappear from every public page, list, search and sitemap.</p>
      <div className="divide-y divide-border">
        {rows.map((s) => (
          <div key={s.id} className="flex items-center justify-between py-2 gap-3" data-testid={`visibility-row-${s.id}`}>
            <div className="min-w-0">
              <div className="font-medium truncate">{s.name}</div>
              <div className="text-xs text-muted-foreground font-mono truncate">{s.slug} · {CLASS_LABEL[s.classification]}{s.hidden ? " · HIDDEN" : ""}</div>
            </div>
            <Button size="sm" variant={s.hidden ? "outline" : "default"} onClick={() => setTarget(s)} data-testid={`visibility-toggle-${s.id}`}>
              {s.hidden ? <><Eye className="w-4 h-4 mr-1" />Unhide</> : <><EyeOff className="w-4 h-4 mr-1" />Hide</>}
            </Button>
          </div>
        ))}
        {rows.length === 0 && <div className="text-sm text-muted-foreground py-2" data-testid="visibility-empty">No test or demo stores found.</div>}
      </div>
      <VisibilityDialog target={target} onClose={() => setTarget(null)} onDone={() => { setTarget(null); load(); }} />
    </div>
  );
};
